import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { projectKeys } from './useProjects';
import type { GanttTask, CreateGanttTaskInput, UpdateGanttTaskInput } from '../types';

export const ganttKeys = {
  all: ['gantt'] as const,
  list: (projectId: string) => [...ganttKeys.all, projectId] as const,
};

// Fetch all gantt tasks for a project
export function useGanttTasks(projectId: string) {
  return useQuery({
    queryKey: ganttKeys.list(projectId),
    queryFn: () => api.get<GanttTask[]>('/gantt', { projectId }),
    enabled: !!projectId,
  });
}

// Create a gantt task
export function useCreateGanttTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGanttTaskInput) => api.post<GanttTask>('/gantt', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ganttKeys.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.projectId) });
    },
  });
}

// Update a gantt task
export function useUpdateGanttTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; projectId: string; data: UpdateGanttTaskInput }) =>
      api.patch<GanttTask>(`/gantt/${variables.id}`, variables.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ganttKeys.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.projectId) });
    },
  });
}

// Delete a gantt task
export function useDeleteGanttTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; projectId: string }) =>
      api.delete<void>(`/gantt/${variables.id}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ganttKeys.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.projectId) });
    },
  });
}
